// src/Payroll_Management/PayRoll_Report/EmployeePerformanceReportPrint.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function EmployeePerformanceReportPrint() {
  const q = useQuery();
  const finYearId = localStorage.getItem("finYearId") || "";
  const empId = q.get("empId") || "";
  const autoPrint = (q.get("autoPrint") || "1") === "1";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ Company header (same style as your other print)
  const company = {
    name: "Nissi Infotech",
    sub: "Nissi",
    line1: "CIVIL CONSTRUCTION",
    addr: "3/42-12, Nagappa colony , kavundampalayam - Coimbatore - Tamil Nadu.",
    phone: "Phone No : 9003744644",
    email: "infraconsoft-implementation",
  };

  const logoUrl = "/General/Theme1/Images/CompanyLogo.PNG";

  const fetchRows = async () => {
    if (!String(finYearId).trim() || !String(empId).trim()) return;

    try {
      setLoading(true);
      // ✅ use your existing listing API for performance list
      const res = await axios.get(`${BASE_URL}/api/employee-report/performance`, {
        params: { finYearId: Number(finYearId), empId: String(empId).trim(), page: 1, pageSize: 10000 },
      });

      if (res?.data?.success) setRows(res.data.rows || []);
      else setRows([]);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empId]);

  useEffect(() => {
    if (!autoPrint) return;
    if (loading) return;
    const t = setTimeout(() => window.print(), 250);
    return () => clearTimeout(t);
  }, [autoPrint, loading]);

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; background: #fff; color: #000; font-family: "Times New Roman", Times, serif; }
        .page { width: 1100px; margin: 12px auto; }

        table { border-collapse: collapse; width: 100%; }
        .outer { border: 1px solid #000; }
        .outer td { border: 1px solid #000; vertical-align: top; }

        .hdrWrap { padding: 10px; }
        .hdrInner { width: 78%; margin: 0 auto; border: 1px solid #000; }
        .hdrInner td { border: none; }
        .logoBox { width: 210px; padding: 6px; }
        .logoBox img { width: 190px; height: 95px; object-fit: contain; display: block; }
        .hdrText { text-align: center; padding: 6px 10px; line-height: 1.15; }
        .hdrText .cname { font-weight: 700; font-size: 22px; }
        .hdrText .sub { font-size: 14px; margin-top: 2px; }
        .hdrText .line1 { font-weight: 700; font-size: 14px; margin-top: 4px; }
        .hdrText .addr { font-size: 13px; margin-top: 6px; }
        .hdrText .phone { font-size: 13px; margin-top: 2px; }
        .hdrText .email { font-size: 13px; margin-top: 2px; }

        .titleRow { text-align: center; font-weight: 700; font-size: 14px; padding: 10px 0; }

        .list th, .list td { border: 1px solid #000; padding: 8px 8px; font-size: 13px; }
        .list th { font-weight: 700; text-align: center; }
        .list td { text-align: center; vertical-align: middle; }
        .list td.left { text-align: left; }

        .muted { font-size: 14px; padding: 18px; text-align: center; }

        .noPrint { display: flex; gap: 8px; justify-content: flex-end; margin: 10px 0; }
        .btn { border: 1px solid #000; background: #fff; padding: 6px 10px; cursor: pointer; font-weight: 700; }
        .btn:active { transform: translateY(1px); }

        @media print {
          .page { width: auto; margin: 0; }
          .noPrint { display: none; }
          @page { size: A4 landscape; margin: 10mm; }
        }
      `}</style>

      <div className="page">
        {/* optional print button (hidden in print) */}
        <div className="noPrint">
          <button className="btn" onClick={() => window.print()}>
            PRINT
          </button>
        </div>

        <table className="outer">
          <tbody>
            {/* header */}
            <tr>
              <td>
                <div className="hdrWrap">
                  <table className="hdrInner">
                    <tbody>
                      <tr>
                        <td className="logoBox">
                          <img
                            src={logoUrl}
                            alt="Company Logo"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        </td>
                        <td className="hdrText">
                          <div className="cname">{company.name}</div>
                          <div className="sub">{company.sub}</div>
                          <div className="line1">{company.line1}</div>
                          <div className="addr">{company.addr}</div>
                          <div className="phone">{company.phone}</div>
                          <div className="email">{company.email}</div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>

            {/* title */}
            <tr>
              <td className="titleRow">EMPLOYEE PERFORMANCE REPORT</td>
            </tr>

            {/* list table */}
            <tr>
              <td style={{ padding: 0 }}>
                {loading ? (
                  <div className="muted">Loading...</div>
                ) : rows.length === 0 ? (
                  <div className="muted">No Records Found</div>
                ) : (
                  <table className="list">
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}>S.No</th>
                        <th style={{ width: 140 }}>Employee Code</th>
                        <th style={{ width: 220 }}>Employee Name</th>
                        <th style={{ width: 140 }}>Date</th>
                        <th style={{ width: 220 }}>Title</th>
                        <th>Performance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={r.fld_Perform_ID ?? idx}>
                          <td>{idx + 1}</td>
                          <td>{r.fld_EmployeeCode || r.fld_Employeecode || ""}</td>
                          <td className="left">{r.fld_Employee || ""}</td>
                          <td>{r.PerformanceDate || ""}</td>
                          <td className="left">{r.fld_title || r.fld_Title || ""}</td>
                          <td className="left" style={{ whiteSpace: "pre-wrap" }}>
                            {r.fld_Details || ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
